/**
 * Auth primitives — Clerk is the sole engine (SSO Wave 3, REK-2166).
 *
 * All call sites that previously reached for `supabase.auth.*` go through
 * these helpers. Clerk-js manages token freshness internally; user identity
 * comes from the ClerkAuthProvider registry (legacy profiles.id UUID).
 */
import {
  getClerkSupabaseToken,
  getClerkAuthUser,
  clerkSignOut,
  type ClerkAuthUser,
} from '@/lib/clerk-token';

export interface AuthBridgeUser {
  /** Legacy `profiles.id` UUID. */
  id: string;
  email: string | null;
}

/**
 * Access token for Authorization headers on manual fetch / functions.invoke
 * calls. Null when signed out. Always freshly minted or cache-valid (clerk-js
 * manages expiry).
 */
export async function getAuthAccessToken(): Promise<string | null> {
  return getClerkSupabaseToken();
}

/**
 * Force-refresh the access token (skip the Clerk token cache).
 * Returns the new token or null when refresh fails.
 */
export async function refreshAuthAccessToken(): Promise<string | null> {
  return getClerkSupabaseToken({ forceRefresh: true });
}

/**
 * The signed-in user's identity, or null when signed out.
 *
 * Returns the legacy UUID + email registered by ClerkAuthProvider after
 * profile resolution — no network round trip, and null until the provider
 * has resolved (same observable shape as "signed out").
 */
export async function getAuthUser(): Promise<AuthBridgeUser | null> {
  const user: ClerkAuthUser | null = getClerkAuthUser();
  return user ? { id: user.id, email: user.email } : null;
}

/**
 * Sign out via Clerk (clerk.signOut() + the provider's storage-clearing
 * semantics).
 */
export async function authSignOut(): Promise<void> {
  await clerkSignOut();
}
